// src/components/tickets/detail/EscalateDialog.tsx
//
// The escalate dialog (Story 4.4 AC 7). One gesture: it previews the NEXT layer
// (position + matcher summary, NEVER a resolved person — AD-2 forbids predicting
// the pick) and offers an OPTIONAL internal note in the SAME dialog. Primary
// "Escalate" confirms. On a business failure the mapped copy shows inline and the
// typed note is preserved (the dialog owns its own note state; the parent keeps
// it mounted and passes the error).
//
// Overlay follows the repo idiom (fixed inset-0, role=dialog, Esc closes, focus
// returns to the trigger — handled by the caller, with a stable fallback). One
// modal level.

import { useEffect, useRef, useState } from 'react'
import { matcherSummary } from '../../../utils/escalation'

interface EscalateDialogProps {
  // The next layer's position label + matcher attributes, read from the rules row.
  nextLayerPosition: string
  nextLayerMatchers: string[]
  // True when the current assignment has no live layer (NULL/stale) — escalation
  // restarts from layer 1, and the copy says so.
  fromStart: boolean
  escalating: boolean
  // Mapped business-failure copy to show inline (null = none); the note survives.
  error: string | null
  onEscalate: (note: string | null) => void
  onCancel: () => void
}

export function EscalateDialog({
  nextLayerPosition,
  nextLayerMatchers,
  fromStart,
  escalating,
  error,
  onEscalate,
  onCancel,
}: EscalateDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  const [note, setNote] = useState('')

  useEffect(() => {
    confirmRef.current?.focus()
  }, [])

  // The next-layer preview line. From layer 1 (NULL/stale current): position only.
  // Otherwise: position + matcher summary. Never a person.
  const previewLine = fromStart
    ? `Escalates along the configured path from layer 1 — ${nextLayerPosition}`
    : `Next layer: ${nextLayerPosition} — ${matcherSummary(nextLayerMatchers)}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="escalate-title"
        aria-describedby={error ? 'escalate-error' : undefined}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
      >
        <h3
          id="escalate-title"
          className="mb-1 text-lg font-semibold text-gray-900"
        >
          Escalate this ticket
        </h3>
        <p className="mb-4 text-sm text-gray-600">{previewLine}</p>

        <label
          htmlFor="escalate-note"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Internal note (optional) — not visible to rep
        </label>
        <textarea
          id="escalate-note"
          value={note}
          onChange={e => setNote(e.target.value)}
          disabled={escalating}
          rows={3}
          className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />

        {error ? (
          <div
            id="escalate-error"
            role="alert"
            className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={escalating}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => onEscalate(note.trim() === '' ? null : note)}
            disabled={escalating}
            className="btn-outline"
          >
            {escalating ? 'Escalating…' : 'Escalate'}
          </button>
        </div>
      </div>
    </div>
  )
}
